import React, { useMemo } from "react"

import { format } from "date-fns"

import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { formatCost } from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import { WeekData, revenueByRoute, revenueDiffs, routesActiveOn } from "@/lib/weeklyPlan"

export type DeltaInsightType = "routes" | "revenue"

function pickDates(weekDays: Date[], dayIndex: number): string[] {
  const dateStrs = weekDays.map((d) => format(d, "yyyy-MM-dd"))
  if (dayIndex < 0) return dateStrs
  return dateStrs[dayIndex] ? [dateStrs[dayIndex]] : []
}

interface DeltaInsightSheetProps {
  open: boolean
  onClose: () => void
  type: DeltaInsightType
  /** Weekday 0 (Monday) to 6, or -1 for the whole week. */
  dayIndex: number
  dayLabel: string
  leftData: WeekData
  rightData: WeekData
  leftWeekDays: Date[]
  rightWeekDays: Date[]
}

const DeltaInsightSheet: React.FC<DeltaInsightSheetProps> = ({
  open,
  onClose,
  type,
  dayIndex,
  dayLabel,
  leftData,
  rightData,
  leftWeekDays,
  rightWeekDays,
}) => {
  const leftDates = useMemo(() => pickDates(leftWeekDays, dayIndex), [leftWeekDays, dayIndex])
  const rightDates = useMemo(
    () => pickDates(rightWeekDays, dayIndex),
    [rightWeekDays, dayIndex],
  )

  const leftRoutes = useMemo(
    () => routesActiveOn(leftData.categories, leftDates),
    [leftData.categories, leftDates],
  )
  const rightRoutes = useMemo(
    () => routesActiveOn(rightData.categories, rightDates),
    [rightData.categories, rightDates],
  )

  const leftCodes = useMemo(() => new Set(leftRoutes.map((r) => r.code)), [leftRoutes])
  const rightCodes = useMemo(() => new Set(rightRoutes.map((r) => r.code)), [rightRoutes])
  const onlyLeft = useMemo(
    () => leftRoutes.filter((r) => !rightCodes.has(r.code)),
    [leftRoutes, rightCodes],
  )
  const onlyRight = useMemo(
    () => rightRoutes.filter((r) => !leftCodes.has(r.code)),
    [rightRoutes, leftCodes],
  )

  const revDiffs = useMemo(
    () =>
      revenueDiffs(
        revenueByRoute(leftData.categories, leftDates),
        revenueByRoute(rightData.categories, rightDates),
      ),
    [leftData.categories, leftDates, rightData.categories, rightDates],
  )

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {type === "routes" ? "Route differences" : "Haulage revenue differences"} — {dayLabel}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {type === "routes" && (
            <>
              {onlyLeft.length === 0 && onlyRight.length === 0 && (
                <p className="text-sm text-palette-jungleGreen500">
                  No route differences between the two weeks.
                </p>
              )}
              {onlyLeft.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-red-700 uppercase mb-2">
                    Removed in Week 2 ({onlyLeft.length})
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {onlyLeft.map((r) => (
                      <Badge
                        key={r.routeId}
                        variant="outline"
                        className="text-xs border-red-300 text-red-700"
                      >
                        {r.code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {onlyRight.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-green-700 uppercase mb-2">
                    Added in Week 2 ({onlyRight.length})
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {onlyRight.map((r) => (
                      <Badge
                        key={r.routeId}
                        variant="outline"
                        className="text-xs border-green-300 text-green-700"
                      >
                        {r.code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {type === "revenue" && (
            <>
              {revDiffs.length === 0 && (
                <p className="text-sm text-palette-jungleGreen500">
                  No haulage revenue differences between the two weeks.
                </p>
              )}
              {revDiffs.length > 0 && (
                <div className="space-y-1.5">
                  {revDiffs.map(({ code, leftRev, rightRev, delta }) => (
                    <div
                      key={code}
                      className="flex items-center justify-between py-2 px-3 rounded-lg border border-palette-jungleGreen100 bg-white"
                    >
                      <span className="text-sm font-medium text-palette-jungleGreen900">
                        {code}
                      </span>
                      <div className="flex items-center gap-3 text-sm tabular-nums">
                        <span className="text-palette-jungleGreen400">{formatCost(leftRev)}</span>
                        <span className="text-palette-jungleGreen300">→</span>
                        <span className="text-palette-jungleGreen400">{formatCost(rightRev)}</span>
                        <span
                          className={cn(
                            "font-semibold",
                            delta > 0 ? "text-green-600" : "text-red-600",
                          )}
                        >
                          {delta > 0 ? "+" : ""}
                          {formatCost(delta)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default DeltaInsightSheet
